import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * True when another `<dialog>` is already open (nested modal, e.g. cart drawer + quick update).
   * Body scroll lock is owned by the outer dialog only.
   */
  #hasOtherOpenDialog() {
    return [...document.querySelectorAll('dialog[open]')].some((d) => d !== this.refs.dialog);
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const isNestedModal = this.#hasOtherOpenDialog();

    if (!isNestedModal) {
      const scrollY = window.scrollY;
      this.#previousScrollY = scrollY;
    }

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (!isNestedModal) {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.#previousScrollY}px`;
      }

      if (this.usesModalDialog) {
        dialog.showModal();
      } else {
        dialog.show();
      }

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;
    if (this.classList.contains('drawer-country-language-dialog') && !this.getCookie('declined_redirect')) {
      this.setCookie('declined_redirect', "true", { days: 1})
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    const anotherDialogStaysOpen = [...document.querySelectorAll('dialog[open]')].some((d) => d !== dialog);

    if (!anotherDialogStaysOpen) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    this.querySelector('video')?.pause();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }

  /*
   Stops the country/language dialog from overlapping klaviyo banner and usablenet button
  */
  get usesModalDialog() {
    return !this.classList.contains('drawer-country-language-dialog');
  }

  /**
   * Gets a cookie value by name.
   *
   * @param {string} name - The cookie name.
   * @returns {string|null} The cookie value or null if not found.
   */
  getCookie = (name) => {
    const cookies = document.cookie ? document.cookie.split('; ') : [];

    for (const cookie of cookies) {
      const [key, ...rest] = cookie.split('=');
      if (decodeURIComponent(key) === name) {
        return decodeURIComponent(rest.join('='));
      }
    }

    return null;
  };

  /**
   * Sets a cookie.
   *
   * @param {string} name - The cookie name.
   * @param {string} value - The cookie value.
   * @param {object} [options] - Optional cookie settings.
   * @param {number} [options.days] - Expiration in days.
   * @param {string} [options.path='/'] - Cookie path.
   * @param {string} [options.domain] - Cookie domain.
   * @param {boolean} [options.secure] - Secure flag.
   * @param {'Strict'|'Lax'|'None'} [options.sameSite='Lax'] - SameSite policy.
   */
  setCookie = (name, value, options = {}) => {
    const {
      days,
      path = '/',
      domain,
      secure,
      sameSite = 'Lax',
    } = options;

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (typeof days === 'number') {
      const date = new Date();
      date.setTime(date.getTime() + days * 864e5);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    if (path) cookieString += `; path=${path}`;
    if (domain) cookieString += `; domain=${domain}`;
    if (secure) cookieString += '; secure';
    if (sameSite) cookieString += `; samesite=${sameSite}`;

    document.cookie = cookieString;
  };
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
